import React, {FC, useEffect, useState} from "react"

import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Loading from "@irati/ui/src/Loading"
import Flex from "@irati/ui/src/Flex"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import CircledConnector from "@irati/ui/src/CircledConnector"
import {Col, Row} from "@irati/ui/src/Layout"
import DragAndDrop from "@irati/ui/src/DragAndDrop"
import {triggerErrorToast} from "@irati/ui/src/ToastAlert/triggerToast"
import adminTracking from "@irati-tracking/admin"

import {RecapContainer, List} from "../../../components/Users/AddUsers/AddPeopleCSV/styled"
import Field from "../../../components/Users/AddUsers/AddPeopleCSV/Field"
import {hangPromise} from "@irati/utils/promise"
import {importOrdersMutation} from "@irati/payrollpay/hooks"

interface Props {
  onSuccess: () => void
}

const UpdateOrdersCSV: FC<Props> = ({onSuccess}) => {
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [file, setFile] = useState<File | undefined>(undefined)
  const theme = useTheme()
  const {formatMessage} = useIntl()

  useEffect(() => {
    if (file) {
      setLoad(hangPromise())
      importOrdersMutation(file)
        .then(() => {
          setLoad(null)
          onSuccess()
        })
        .catch(() => {
          setLoad(null)
          triggerErrorToast(formatMessage({id: "admin.components.orderManagement.updateOrders.toastError"}))
        })
    }
  }, [file])

  const handleDrop = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0])
    adminTracking.orderManagementImportUpload()
  }

  return (
    <>
      <Flex justifyContent="flex-start" direction="column">
        <H2 marginBottom={2}>
          <FormattedMessage id="admin.components.orderManagement.updateOrders.title" />
        </H2>
        <H4>
          <FormattedMessage id="admin.components.orderManagement.updateOrders.text" />
        </H4>
        <H4 marginBottom={2} color={theme.colors.error}>
          <FormattedMessage id="admin.components.orderManagement.updateOrders.text2" />
        </H4>
        <CircledConnector
          data={[
            formatMessage({id: "admin.components.orderManagement.updateOrders.downloadData"}),
            "renderCompFromProps",
            formatMessage({id: "admin.components.updatePeopleCSV.circledConnector.dragAndDrop"})
          ]}
          comp={[
            <div />,
            <Body maxWidth="100%">
              <FormattedMessage
                id="admin.components.orderManagement.updateOrders.circledConnector.fillInformation"
                values={{
                  warning: (
                    <Body weight="semibold" color={theme.colors.utility.error} maxWidth="100%">
                      <FormattedMessage id="admin.components.orderManagement.updateOrders.circledConnector.warning" />
                    </Body>
                  )
                }}
              />
            </Body>
          ]}
        />
        <Row>
          <Col md={12}>
            <Loading load={load}>
              <DragAndDrop onDrop={handleDrop} />
            </Loading>
            <RecapContainer>
              <H4>
                <FormattedMessage id="admin.components.orderManagement.updateOrders.fieldsMapping.title" />
              </H4>
              <List>
                <Field
                  label="admin.components.orderManagement.updateOrders.fieldsMapping.nino.label"
                  description="admin.components.orderManagement.updateOrders.fieldsMapping.desc"
                />
                <Field
                  label="admin.components.orderManagement.updateOrders.fieldsMapping.employeeNum.label"
                  description="admin.components.orderManagement.updateOrders.fieldsMapping.desc"
                />
                <Field
                  label="admin.components.orderManagement.updateOrders.fieldsMapping.orgApproval.label"
                  description="admin.components.orderManagement.updateOrders.fieldsMapping.desc"
                />
                <Field
                  label="admin.components.orderManagement.updateOrders.fieldsMapping.rejectionReason.label"
                  description="admin.components.orderManagement.updateOrders.fieldsMapping.desc"
                />
                <Field
                  label="admin.components.orderManagement.updateOrders.fieldsMapping.comment.label"
                  description="admin.components.orderManagement.updateOrders.fieldsMapping.desc"
                />
              </List>
            </RecapContainer>
          </Col>
        </Row>
      </Flex>
    </>
  )
}

export default UpdateOrdersCSV
